import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useEditorStore } from '../store/useEditorStore';

function computeEditorBox(stageContainerRef, node, zoomScale) {
  const el = stageContainerRef?.current;
  if (!el || !node || node.type !== 'text') return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const logicalW = Number(el.offsetWidth) || rect.width;
  const logicalH = Number(el.offsetHeight) || rect.height;
  const scaleX = logicalW > 0 ? rect.width / logicalW : zoomScale || 1;
  const scaleY = logicalH > 0 ? rect.height / logicalH : zoomScale || 1;
  const t = node.transform;
  return {
    left: rect.left + (t.x || 0) * scaleX,
    top: rect.top + (t.y || 0) * scaleY,
    width: Math.max(40, (t.width || 100) * scaleX),
    height: Math.max(24, (t.height || 40) * scaleY),
    fontScale: Math.min(scaleX, scaleY),
  };
}

/**
 * HTML textarea over the active text box — native typing, no Fabric/Konva text scaling.
 */
export default function HtmlTextOverlay({ stageContainerRef, zoomScale = 1 }) {
  const editingTextId = useEditorStore((s) => s.editingTextId);
  const node = useEditorStore((s) =>
    s.editingTextId ? s.scene.objects.find((o) => o.id === s.editingTextId) : null,
  );
  const endTextEdit = useEditorStore((s) => s.endTextEdit);
  const updateTextNode = useEditorStore((s) => s.updateTextNode);
  const textareaRef = useRef(null);
  const box = node ? computeEditorBox(stageContainerRef, node, zoomScale) : null;

  useLayoutEffect(() => {
    if (editingTextId && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editingTextId, box?.left, box?.top, box?.width, box?.height]);

  useEffect(() => {
    if (!editingTextId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        endTextEdit();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [editingTextId, endTextEdit]);

  if (!editingTextId || !node || !box) return null;

  const ty = node.typography || {};
  const layoutFs = node.layout?.fontSize || ty.fontSize || 12;
  const fs = layoutFs * (box.fontScale || zoomScale || 1);

  return createPortal(
    <textarea
      ref={textareaRef}
      className="konva-html-text-editor"
      value={node.text ?? ''}
      onChange={(e) => updateTextNode(editingTextId, { text: e.target.value })}
      onBlur={() => endTextEdit()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        zIndex: 10050,
        margin: 0,
        padding: '4px 6px',
        border: '2px solid #2a318b',
        borderRadius: 4,
        outline: 'none',
        resize: 'none',
        overflow: 'auto',
        background: 'rgba(255,255,255,0.98)',
        color: ty.fill || '#111827',
        fontFamily: ty.fontFamily || 'Arial',
        fontSize: `${fs}px`,
        fontWeight: ty.fontWeight || 'normal',
        fontStyle: ty.fontStyle || 'normal',
        textDecoration: ty.underline ? 'underline' : 'none',
        textAlign: ty.textAlign || 'left',
        lineHeight: ty.lineHeight ?? 1.16,
        boxSizing: 'border-box',
      }}
      spellCheck
    />,
    document.body,
  );
}
